import { useEffect, useRef, useState } from "react"

import { ChevronRight } from "lucide-react"
import type { LucideIcon } from "lucide-react"

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@ui/collapsible"
import { Textarea } from "@ui/textarea"
import { cn } from "@/lib/utils"

const DEBOUNCE_DELAY = 1000

interface SearchSectionProps {
    label: string
    placeholder: string
    Icon?: LucideIcon
    onSelection?: (selection: any) => void
    onClear: number
}
export function SearchSection({ label, placeholder, Icon, onSelection, onClear }: Readonly<SearchSectionProps>) {
    const [vectorText, setVectorText] = useState<string>("")

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

    const handleOnSelectionChange = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        onSelection?.(!vectorText?.length ? undefined : vectorText)
    }

    const handleVectorTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value
        setVectorText(value)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
            onSelection?.(!value?.length ? undefined : value)
        }, DEBOUNCE_DELAY)
    }

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        e.stopPropagation()
        if (e.key === "Enter") {
            e.preventDefault()
            handleOnSelectionChange()
        }
    }

    useEffect(() => {
        setVectorText("")

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }
    }, [onClear])

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [])

    return (
        <div className={cn("relative flex w-full min-w-0 flex-col p-2 border-b border-sidebar-border")}>
            <Collapsible defaultOpen={false} className="group/collapsible">
                <CollapsibleTrigger className={cn("group/label w-full text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", "flex h-8 shrink-0 items-center rounded-md px-2 text-sm font-medium outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0", "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 hover:cursor-pointer mb-1")}>
                    {Icon && <Icon className="mr-2" />}
                    {label} <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <Textarea
                        value={vectorText}
                        onChange={handleVectorTextChange}
                        onKeyDown={handleInputKeyDown}
                        placeholder={placeholder}
                    />
                </CollapsibleContent>
            </Collapsible>
        </div>
    )
}